import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo } from 'react';
import { PROJECT_FILTER_TYPE } from '../../constants/constants';
import { apiEditRoom, apiGetGlobalTags, apiGetRooms, apiGetRoomV2 } from '../../helpers/api';
import { getProjectTypeLabel } from '../../helpers/dataHelper';
import { useFilterDropdownOptions } from '../../hooks/useFilterDropdownOptions';
import { useRoomTypeOptions } from '../../hooks/useRoomTypes';
import { EditPopupUI } from '../EditPopupUI/EditPopupUI';
import { EditSection } from '../EditSection/EditSection';
import {
  getCategoryFields,
  getNameFields,
  getProjectTypeFields,
  getTagsFields,
  nameSchema,
} from './fieldsConfig';
import { FormProvider, useForm } from 'react-hook-form';
import { errorToast, successToast } from '../../helpers/toastHelper';
import { Form } from 'react-bootstrap';
import { EditPopupButtonsUI } from '../EditPopupUI/EditPopupButtonsUI';
import { createGlobalTagAction } from '../../api/commonApi';
import { useDispatch, useSelector } from 'react-redux';
import memoize from 'memoizee';
import {
  editRoomDetailsPopupActions,
  selectEditRoomDetailsPopupData,
} from '../../redux/slicers/editRoomDetailsPopupSlicer';
import { useApi } from '../../hooks/useApi';

// export const EditRoomPopup = ({ data, show, onClose, loading, onUpdateSuccess }) => {
export const EditRoomPopup = ({ onUpdateSuccess }) => {
  // const formVisible = show && data?.id;
  const dispatch = useDispatch();
  const roomInfo = useSelector(selectEditRoomDetailsPopupData);
  const thumbnail = roomInfo?.thumbnail;
  const roomId = roomInfo?.id;
  const show = Boolean(roomId);

  const {
    data,
    loading,
    update: updateRoomDetails,
  } = useApi({
    api: apiGetRoomV2,
    id: roomId,
    skip: !show,
  });

  if (!data) return null;

  const formVisible = show && data?.id;

  const { owner_organization_details } = data || {};
  let subtitle = owner_organization_details?.name;

  const onClose = () => {
    dispatch(editRoomDetailsPopupActions.close());
  };

  const handleUpdateSuccess = (response) => {
    updateRoomDetails(response);
    onUpdateSuccess(response);
    onClose();
  };

  return (
    <EditPopupUI
      show={show}
      onClose={onClose}
      loading={loading}
      title={data?.name}
      subtitle={subtitle}
      thumbnail={thumbnail}>
      {formVisible && (
        <EditRoomForm
          data={data}
          show={show}
          onClose={onClose}
          loading={loading}
          onUpdateSuccess={handleUpdateSuccess}
        />
      )}
    </EditPopupUI>
  );
};

const getSearchTagOption = (item) => ({
  value: typeof item === 'string' ? item : item.name,
  label: typeof item === 'string' ? item : item.name,
});

// WHY DO WE NEED THIS POPUP IF PROJECT POPUP WORKS IN THE SAME WAY -> THE ONLY DIFFERENCE IS API REQUEST???
export const EditRoomForm = ({ data: roomInfo, show, onClose, loading, onUpdateSuccess }) => {
  const { roomTypeOptions } = useRoomTypeOptions();
  const dispatch = useDispatch();

  const projectFilters = useFilterDropdownOptions({
    filterType: PROJECT_FILTER_TYPE,
    fetchData: show,
  });

  const getFilterValue = (filterId) => {
    if (!roomInfo?.filteroptions || !projectFilters) return [];

    const ids = roomInfo.filteroptions.map((o) => o.id);
    const filter = projectFilters.find((filter) => filter.id === filterId);
    const value = filter.options.filter((category) => ids.includes(category.value));
    return value;
  };

  const memoizedSearch = useCallback(async (search, page) => {
    const response = await apiGetGlobalTags({ search, page });

    return {
      options: response.results.map(getSearchTagOption),
      hasMore: Boolean(response.next_link),
      additional: {
        page: page + 1,
      },
    };
  }, []);

  const loadSearchTagOptions = async (search, loadedOptions, { page }) => {
    return memoizedSearch(search, page);
  };

  const tagsValue = roomInfo?.tags.map(getSearchTagOption);

  const handleCreateTag = async (newTagName) => {
    const val = newTagName.trim();
    const result = await dispatch(createGlobalTagAction({ name: val }));
    if (!result?.data?.id) return;

    const existingTags = getValues('tags');
    const existingValues = existingTags.map((a) => a.value);

    setValue('tags', [...existingValues, result?.data?.name].map(getSearchTagOption));
  };

  const { nameFields, tagsFieldsConfig, projectTypeFields, defaultValues, schema } = useMemo(() => {
    if (!roomInfo?.id) return {};

    let schemaObj = {
      ...nameSchema,
    };

    const tagsFieldsConfig = getTagsFields(tagsValue, loadSearchTagOptions, handleCreateTag);

    const nameFields = getNameFields(roomInfo);
    const projectTypeFields = getProjectTypeFields(roomInfo, roomTypeOptions);

    const fields = [...nameFields, ...tagsFieldsConfig.fieldsConfig, ...projectTypeFields];

    projectFilters.map((filter) => {
      const { fieldsConfig, schema } = getCategoryFields(filter, getFilterValue(filter.id));
      fields.push(...fieldsConfig);
      schemaObj = { ...schemaObj, ...schema };
    });

    // fields.push(...tagsFieldsConfig.fieldsConfig)
    schemaObj = { ...schemaObj, ...tagsFieldsConfig.schema };

    const defaultValues = fields.reduce((acc, field) => {
      if (!field.name) return acc;

      acc[field.name] = field.value;
      return acc;
    }, {});

    return {
      nameFields,
      tagsFieldsConfig,
      projectTypeFields,
      defaultValues,
      schema: yup.object(schemaObj),
    };
  }, [roomInfo, roomInfo.tags, roomTypeOptions, projectFilters]);

  const methods = useForm({
    resolver: schema ? yupResolver(schema) : undefined,
    defaultValues,
    mode: 'onChange',
  });

  const {
    getValues,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  const onInvalid = () => {
    errorToast('Please fix errors', { toastId: 'TOAST_FIX_ERRORS' });
  };

  const onSubmit = async (values) => {
    if (!isDirty) {
      onClose();
      return;
    }

    const updatedData = { filteroptions: [], tags: [] };

    Object.keys(values).forEach((key) => {
      if (key.startsWith('filter_')) {
        const options = values[key];
        updatedData.filteroptions.push(...options.map((c) => c.value));
      } else if (key === 'tags') {
        const options = values[key];
        updatedData.tags.push(...options.map((c) => c.value));
      } else {
        updatedData[key] = values[key];
      }
    });
    const formData = new FormData();
    Object.keys(updatedData).forEach((key) => {
      let value = updatedData[key];
      if ((key === 'filteroptions' || key === 'tags') && !value?.length) {
        value = "[]";
      }
      formData.append(key, value);
    });
    const response = await apiEditRoom(roomInfo.id, formData, null, null, false);

    if (response) {
      onUpdateSuccess?.(response.data);
    }
  };

  return (
    <>
      {!loading && roomInfo && (
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit, onInvalid)} autoComplete="off">
            <EditSection title="Room name" fields={nameFields} />

            <EditSection title="Type of project" fields={projectTypeFields} />

            {projectFilters.map((filter) => {
              const { fieldsConfig } = getCategoryFields(filter, getFilterValue(filter.id));
              return (
                <EditSection
                  key={filter.id}
                  title={filter.label_name}
                  fields={fieldsConfig}
                  previewMode="fields"
                />
              );
            })}

            {/* tags */}
            <EditSection title="Tags" fields={tagsFieldsConfig.fieldsConfig} previewMode="fields" />

            <EditPopupButtonsUI loading={isSubmitting} onCacel={onClose} />
          </Form>
        </FormProvider>
      )}
    </>
  );
};
